<template>
  <div class="mb-20">
    <form class="basic-details-section" @submit.prevent="saveQuestion">
      <div class="flex justify-between items-center">
        <h1>{{ editMode ? "Edit" : "Create" }} Project Question</h1>
        <button
          class="cursor-pointer rounded p-2 px-6
              font-semibold text-white
              bg-blue
              hover:shadow-md"
          type="submit"
        >
          {{ editMode ? "Update" : "Create" }} Question (Ctrl + s)
        </button>
      </div>
      <label for="name">Name <span class="required">*</span></label>
      <input
        id="name"
        v-model="questionObject.name"
        required
        placeholder="Enter question name"
        class="text-input w-full"
        type="text"
      />
      <label for="repo"
        >Repo Name (add only Github Repo name. Ex: edyst-netflix-pass)<span
          class="required"
          >*</span
        ></label
      >
      <!-- regex below doesn't allow these keywords in repo name
      git@, github., http://, https://, .com, /, :    -->
      <input
        id="repo"
        v-model="questionObject.data.repo"
        v-validate="{
          required: true,
          regex: /^(?!.*git@|.*github\.|.*http:\/\/|.*https:\/\/|.*\.com|.*\/|.*:)/,
        }"
        required
        placeholder="Enter URL of project template (github)"
        class="text-input w-full"
        type="text"
        name="repo name"
      />
      <small class="err-msg text-red block mt-2">
        {{ veeValidateErrors.first("repo name") }}
      </small>
      <label for="invitation link"
        >Invitation URL (please enter full URL. Ex:
        https://classroom.github.com/a/BGP0YMhl)<span class="required"
          >*</span
        ></label
      >
      <!-- regex below only allows link that starts with
       https://classroom.github.com/   -->
      <input
        id="invitation link"
        v-model="questionObject.data.invitationLink"
        v-validate="{
          required: true,
          regex: /^(https:\/\/classroom\.github\.com\/.*)$/,
          min: 35,
        }"
        required
        placeholder="Enter invitation link of the project"
        class="text-input w-full"
        type="url"
        name="invitation link"
      />
      <small class="err-msg text-red block mt-2">
        {{ veeValidateErrors.first("invitation link") }}
      </small>
      <label for="points">Points<span class="required">*</span></label>
      <input
        id="points"
        v-model="questionObject.points"
        required
        placeholder="Enter question points"
        class="text-input w-full"
        type="number"
      />
      <div class="w-full">
        <label class="mr-10 text-lg">Tags</label>
        <div class="w-full">
          <VueSelect
            v-model="selectedTags"
            :options="tagOptions"
            label="name"
            multiple
          />
        </div>
      </div>
    </form>
    <div class="flex mt-8 rounded-sm shadow tab-container">
      <button
        v-for="(tab, index) in ['Content', 'Test Cases']"
        :key="tab"
        class="w-1/2 text-center tab rounded-sm"
        :class="`${activeTab === tab ? 'tab-active' : ''}`"
        :title="`${tab} (Num: ${index + 1})`"
        @click="activeTab = tab"
      >
        {{ tab }} {{ getTabItemCount(tab) }}
      </button>
    </div>
    <section v-show="activeTab === 'Content'" class="content-section">
      <div class="mt-10 flex border p-2">
        <div class="ml-2">
          <h4 class="mt-1 font-bold text-md text-left">
            Content
          </h4>
          <MarkdownReadingEditor
            v-model="questionObject.content"
            @imageAdded="catchImageAddedEvent"
          />
        </div>
        <div class="overflow-auto w-full">
          <div class="markdown-body px-4">
            <h4 class="mt-1 font-bold text-md text-left">
              Output
            </h4>
            <VueMarkdown :source="getRenderedMarkdownToHTML()" />
          </div>
        </div>
      </div>
    </section>
    <section v-show="activeTab === 'Test Cases'" class="content-section mt-10">
      <div
        v-for="(section, sectionIndex) in questionObject.submission_cases
          .sections"
        :key="sectionIndex + '-section'"
        tabindex="0"
        class="border p-4 shadow section-item mt-2 relative"
        @click="workingSectionIndex = sectionIndex"
        @focus="workingSectionIndex = sectionIndex"
      >
        <div>
          <label :for="`section-${sectionIndex}`" class="pt-0"
            >Section Title</label
          >
          <input
            :id="`section-${sectionIndex}`"
            v-model="section.title"
            type="text"
            class="w-2/3 section-title"
          />
        </div>
        <button
          class="remove-icon ml-4 cursor-pointer"
          @click="removeSection(sectionIndex)"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            class=""
            width="14"
            height="14"
            viewBox="0 0 24 24"
          >
            <path
              d="M18.5 15c-2.484 0-4.5 2.015-4.5 4.5s2.016 4.5 4.5 4.5c2.482 0 4.5-2.015 4.5-4.5s-2.018-4.5-4.5-4.5zm2.5 5h-5v-1h5v1zm-5-11v4.501c-.748.313-1.424.765-2 1.319v-5.82c0-.552.447-1 1-1s1 .448 1 1zm-4 0v10c0 .552-.447 1-1 1s-1-.448-1-1v-10c0-.552.447-1 1-1s1 .448 1 1zm1.82 15h-11.82v-18h2v16h8.502c.312.749.765 1.424 1.318 2zm-6.82-16c.553 0 1 .448 1 1v10c0 .552-.447 1-1 1s-1-.448-1-1v-10c0-.552.447-1 1-1zm14-4h-20v-2h5.711c.9 0 1.631-1.099 1.631-2h5.316c0 .901.73 2 1.631 2h5.711v2zm-1 2v7.182c-.482-.115-.983-.182-1.5-.182l-.5.025v-7.025h2z"
            />
          </svg>
        </button>
        <div v-for="(item, testCaseIndex) in section.tests" :key="item.id">
          <ProjectQuestionTestCase
            :testCase="item"
            :index="testCaseIndex"
            :sectionIndex="sectionIndex"
            @update-user-input="updateUserInput"
            @remove-test-case="removeTestCase"
          />
        </div>
        <div v-if="section.tests && section" class="text-right mt-2">
          <button
            title="Add New Test Case or (c on keyboard)"
            class="rounded px-3 py-2 bg-purple-dark hover:bg-purple-darker text-white cursor-pointer font-normal"
            @click="addNewTestCase"
          >
            + Add Test Case (c)
          </button>
        </div>
      </div>
      <div class="text-right mt-2">
        <button
          title="Add New Test Case or (c on keyboard)"
          class="rounded px-3 py-2 bg-blue hover:bg-blue-dark text-white cursor-pointer font-normal"
          @click="addNewSection"
        >
          + Add Section (s)
        </button>
      </div>
    </section>
  </div>
</template>

<script>
import VueMarkdown from "vue-markdown"
import VueSelect from "vue-select"
import MarkdownReadingEditor from "@/components/questions/MarkdownReadingEditor"
import markdownHelpers from "@/utils/markdownEditorHelpers"
import siteConfig from "@/utils/siteConfig"
import ProjectQuestionTestCase from "@/components/ProjectQuestion/ProjectQuestionTestCase"

export default {
  components: {
    VueMarkdown,
    MarkdownReadingEditor,
    VueSelect,
    ProjectQuestionTestCase,
  },
  props: {
    editMode: {
      type: Boolean,
      default: false,
      required: false,
    },
  },
  data() {
    return {
      activeTab: "Content",
      markdownRenderer: undefined,
      previousTestCasePoints: 0,
      previousTestCaseRequiredState: true,
      previousTestCaseEvalState: "automatic",
      workingSectionIndex: 0, // 0 default
      questionObject: {
        name: "",
        content: "Content here",
        type: 12,
        points: 0,
        attachments: [],
        qtags: [],
        data: {
          repo: "",
          invitationLink: "",
        },
        submission_cases: {
          sections: [],
        },
      },
      selectedTags: [],
      tagOptions: [],
    }
  },
  computed: {
    testCasesCount() {
      let count = 0
      const { sections } = this.questionObject.submission_cases
      if (sections && Array.isArray(sections) && sections.length > 0) {
        sections.forEach(s => {
          if (s.tests && Array.isArray(s.tests)) {
            count += s.tests.length
          }
        })
      }
      return count
    },
  },
  created() {
    if (this.editMode && this.$route.params.id) {
      this.getQuestion()
    } else {
      /* add a default section for new questions */
      this.questionObject.submission_cases.sections.push({
        title: `Overall`,
        tests: [
          {
            title: "",
            points: 0,
            is_required: true,
            eval: "automatic",
          },
        ],
      })
    }
    this.markdownRenderer = window.markdownit()
    this.populateTags()
  },
  mounted() {
    this.$el.querySelector("#name").focus()
    document.addEventListener("keydown", this.handleKeydown, false)
  },
  methods: {
    getQuestion() {
      this.axios({
        method: "get",
        url: `${siteConfig.siteURL}questions/${this.$route.params.id}`,
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          "x-access-token": localStorage.token,
        },
      })
        .then(res => {
          this.questionObject = res.data.data
          this.selectedTags = res.data.data.qtags
          window.pageTitle = `Edit - ${this.questionObject.name}`
        })
        .catch(err => {
          this.$noty.error(`Failed to fetch question details ${err}`)
          console.error(err)
        })
    },
    getAbsentItemsFromArray(arrayToFindIn = [], arrayToFindFrom = []) {
      const list = []
      arrayToFindFrom.forEach(item => {
        if (!arrayToFindIn.includes(item)) {
          list.push(item)
        }
      })
      return list
    },
    updateUserInput({ sectionIndex, index, key, value }) {
      this.questionObject.submission_cases.sections[sectionIndex].tests[index][
        key
      ] = value
      /* store last modified states so that new test case
        can have same value by deafult */
      if (key === "points") {
        this.previousTestCasePoints = value
      } else if (key === "eval") {
        this.previousTestCaseEvalState = value
      } else if (key === "is_required") {
        this.previousTestCaseRequiredState = value
      }
    },
    getTabItemCount(tab) {
      if (tab === "Test Cases") {
        return `(${this.testCasesCount})`
      }
      return ""
    },
    handleKeydown(e) {
      if (e.which === 83 && e.ctrlKey) {
        this.saveQuestion()
        e.preventDefault()
        return
      }
      if (
        e.target.nodeName !== "INPUT" &&
        e.target.nodeName !== "TEXTAREA" &&
        e.target.nodeName !== "SELECT" &&
        (!e.ctrlKey && !e.shiftKey)
      ) {
        if (this.activeTab === "Test Cases") {
          if (e.which === 67) {
            this.addNewTestCase()
          } else if (e.which === 83) {
            this.addNewSection()
          }
        } else if (e.which === 49) {
          this.activeTab = "Content"
        } else if (e.which === 50) {
          this.activeTab = "Test Cases"
        }
      }
    },
    addNewSection() {
      this.questionObject.submission_cases.sections.push(
        this.getSectionTemplate()
      )
      this.$nextTick(() => {
        const sectionCount = this.questionObject.submission_cases.sections
          .length
        this.workingSectionIndex = sectionCount - 1
        this.addNewTestCase()
      })
    },
    addNewTestCase() {
      this.questionObject.submission_cases.sections[
        this.workingSectionIndex
      ].tests.push(this.getTestCaseTemplate())
    },
    getTestCaseTemplate() {
      return {
        title: "",
        points: this.previousTestCasePoints,
        is_required: this.previousTestCaseRequiredState,
        eval: this.previousTestCaseEvalState,
      }
    },
    getSectionTemplate() {
      return {
        title: `Section ${this.questionObject.submission_cases.sections.length +
          1}`,
        tests: [],
      }
    },
    removeSection(index) {
      this.$swal({
        text: `Are you sure? this will remove all test cases of the section`,
        icon: "warning",
        buttons: {
          cancel: true,
          confirm: {
            text: "Remove",
            value: true,
          },
        },
      }).then(value => {
        if (value) {
          this.questionObject.submission_cases.sections.splice(index, 1)
        }
      })
    },
    removeTestCase({ sectionIndex, index }) {
      this.questionObject.submission_cases.sections[sectionIndex].tests.splice(
        index,
        1
      )
    },
    populateTags() {
      this.axios({
        method: "get",
        url: `${siteConfig.siteURL}qtags/?pagination=false`,
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          "x-access-token": localStorage.token,
        },
      }).then(res => {
        this.tagOptions = res.data.items.map(item => ({
          id: item.id,
          name: item.name,
        }))
      })

      this.selectedTags = this.questionObject.qtags.map(item => ({
        id: item.id,
        name: item.name,
      }))
    },
    getRenderedMarkdownToHTML() {
      return this.markdownRenderer.render(
        markdownHelpers.decodeIDsToMarkdownLinks(
          this.questionObject.content,
          this.questionObject.attachments
        )
      )
    },
    catchImageAddedEvent(eventObj) {
      this.content = eventObj.encodedString
      for (let i = 0; i < eventObj.attachments.length; i++) {
        this.projectObject.attachments.push(eventObj.attachments[i])
      }
    },
    saveQuestion() {
      if (this.editMode) {
        /* returns tag ids that are present in currently selected list
       but not in previously selected list */
        const addedTags = this.getAbsentItemsFromArray(
          this.questionObject.qtags.map(t => t.id),
          this.selectedTags.map(t => t.id)
        )

        /* returns tag ids that are not present in previously selected list
       but in currently selected list */
        const deletedTags = this.getAbsentItemsFromArray(
          this.selectedTags.map(t => t.id),
          this.questionObject.qtags.map(t => t.id)
        )
        this.questionObject.ops_qtags = addedTags
        this.questionObject.ops_qtags_unlink = deletedTags
      } else {
        /* directly add tag ids during question creation */
        this.questionObject.ops_qtags = this.selectedTags.map(t => t.id)
      }

      const url = this.editMode
        ? `${siteConfig.siteURL}questions/${this.$route.params.id}`
        : `${siteConfig.siteURL}questions/`
      const method = this.editMode ? "PUT" : "POST"

      this.axios({
        method,
        url,
        data: this.questionObject,
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          "x-access-token": localStorage.token,
        },
      }).then(res => {
        this.$noty.success(res.data.message)
        setTimeout(() => {
          this.$router.push({ name: "ProjectQuestionList" })
        }, 500)
      })
    },
  },
}
</script>

<style scoped>
.basic-details-section input {
  border: 1px solid #c7c7c7;
}

.basic-details-section input:active,
.basic-details-section input:focus {
  border: 1px solid #c7c7c7;
  outline: 2px solid #74caff;
}

.tab-container {
  width: 50%;
}

.required {
  margin-left: 0.3rem;
  color: red;
}

.section-item input {
  color: #5a5a5a;
}

.section-item input:active,
.section-item input:focus {
  border: 1px solid #c7c7c7;
  outline: 2px solid #74caff;
}

.section-item:focus {
  outline: 2px solid #74caff;
}

.remove-icon {
  position: absolute;
  top: 0.5rem;
  right: 0.5rem;
  width: 1.5rem;
  height: 1.5rem;
  padding: 0;
  border-radius: 0;
}

.remove-icon > svg {
  width: 1.5rem;
  height: 1.5rem;
  fill: red;
}
.remove-icon:hover,
.remove-icon:focus {
  background: transparent;
}

.remove-icon:hover > svg {
  fill: #c20000;
}
</style>
